import {
    useMemo,
    useRef,
    useState,
} from 'react'
import Typography from '@mui/material/Typography'

import Card from 'app/shared-components/Card'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    ContainerAvailabilityAllowedProductTypes,
} from 'app/enums/label.enums'
import {
    ContainerAvailabilityForecastContainerStatuses,
    ContainerAvailabilityForecastIncludeOptions,
} from 'app/enums/common.enums'
import useLabel from 'app/hooks/useLabel'

import {
    ForecastAvailabilityOptions,
} from '../../serviceCenters.types'
import useGetAvailability from '../../hooks/useGetAvailability'
import useStyles from './ContainerAvailabilityForecast.styles'
import ContainerAvailabilityForecastChart from './ContainerAvailabilityForecastChart'
import OptionPanel from './OptionPanel'
import AggregatedChart from './AggregatedChart'

const ContainerAvailabilityForecast = ({
    id,
    defaultProductType,
    productTypes,
}: Readonly<{
    id: number,
    defaultProductType: ContainerAvailabilityAllowedProductTypes,
    productTypes: Record<string, string>,
}>) => {
    const l = useLabel()
    const {
        classes,
    } = useStyles()
    const allowedValues = {
        ALL: 'ALL',
        ...productTypes,
    }
    const [
        value,
        setValue,
    ] = useState<ForecastAvailabilityOptions>({
        containerStatus: {
            [ContainerAvailabilityForecastContainerStatuses.ToBeProcessed]: true,
            [ContainerAvailabilityForecastContainerStatuses.InProcess]: true,
            [ContainerAvailabilityForecastContainerStatuses.Processed]: true,
            [ContainerAvailabilityForecastContainerStatuses.InPreconditioning]: true,
            [ContainerAvailabilityForecastContainerStatuses.Preconditioned]: true,
        },
        productType: defaultProductType,
    })

    const [
        includesOrders,
        setIncludesOrders,
    ] = useState<Record<ContainerAvailabilityForecastIncludeOptions, boolean>>({
        [ContainerAvailabilityForecastIncludeOptions.IncludeNotStartedOrders]: false,
        [ContainerAvailabilityForecastIncludeOptions.IncludePreBookings]: false,
        [ContainerAvailabilityForecastIncludeOptions.IncludeAllDualContainers]: true,
    })

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetAvailability({
        serviceCenter: {
            id,
        },
        ...includesOrders,
    })

    const listStatuses = useRef(Object.keys(value.containerStatus))

    const dataChart = useMemo(() => {
        return data?.forecastsByProduct?.find(({
            productType,
        }) => {
            return productType === value.productType
        })
    }, [
        data?.forecastsByProduct,
        value.productType,
    ])

    return (
        <Card
            contentClass={classes.cardContent}
            data-testid="availability-forecast-card"
            title={l('CONTAINER_AVAILABILITY_FORECAST')}
        >
            <OptionPanel
                value={value}
                onChange={setValue}
                allowedValues={allowedValues}
                includesOrders={includesOrders}
                setIncludesOrders={setIncludesOrders}
            />
            <StatusHandler
                isSuccess={isDataReady}
                isFetching={isFetching}
                isError={isError}
                error={error}
            >
                <>
                    <ContainerAvailabilityForecastChart
                        value={dataChart?.forecast}
                        listStatuses={listStatuses.current}
                        containerStatus={value.containerStatus}
                        minPreconditioned={dataChart?.minPreconditioned}
                    />
                    <Typography
                        variant="h3"
                        className={classes.title}
                    >
                        {l('TOTAL_USABLE_CONTAINERS')}
                    </Typography>
                    <AggregatedChart
                        value={dataChart?.forecast}
                        containerStatus={value.containerStatus}
                        safeMinimumUsableStock={dataChart?.minUsable}
                    />
                </>
            </StatusHandler>
        </Card>
    )
}

export default ContainerAvailabilityForecast
